import React from "react";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";

const DEFAULT_TOP_TEXT =
  "VaultGrid is currently learning which people and events fit you better.";
const DEFAULT_BOTTOM_TEXT =
  "Please attend a few more events and complete next steps to help VaultGrid finalize your social preferences.";

function getInitials(user) {
  const name = String(user?.username || user?.name || "").trim();
  if (!name) return "VG";
  return name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

export function SocialCalibrationAnimation({ user = null, className = "" }) {
  const avatarUrl =
    resolveUserAvatarSrc(user) ||
    user?.avatarUrl ||
    user?.photo_url ||
    user?.photo ||
    "";
  const initials = getInitials(user);

  return (
    <div className={`vg-social-calibration-visual ${className}`} aria-hidden="true">
      <div className="vg-social-calibration-ring vg-social-calibration-ring--outer" />
      <div className="vg-social-calibration-ring vg-social-calibration-ring--middle" />
      <div className="vg-social-calibration-ring vg-social-calibration-ring--inner" />
      <div className="vg-social-calibration-ray vg-social-calibration-ray--one" />
      <div className="vg-social-calibration-ray vg-social-calibration-ray--two" />
      <div className="vg-social-calibration-ray vg-social-calibration-ray--three" />
      <div className="vg-social-calibration-orbit vg-social-calibration-orbit--one">
        <span />
      </div>
      <div className="vg-social-calibration-orbit vg-social-calibration-orbit--two">
        <span />
      </div>
      <div className="vg-social-calibration-orbit vg-social-calibration-orbit--three">
        <span />
      </div>
      <div className="vg-social-calibration-node vg-social-calibration-node--one" />
      <div className="vg-social-calibration-node vg-social-calibration-node--two" />
      <div className="vg-social-calibration-node vg-social-calibration-node--three" />
      <div className="vg-social-calibration-center">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <span>{initials}</span>
        )}
      </div>
    </div>
  );
}

export default function SocialCalibrationContent({
  user = null,
  topText = DEFAULT_TOP_TEXT,
  bottomText = DEFAULT_BOTTOM_TEXT,
  visualClassName = "",
  topTextClassName = "",
  bottomTextClassName = "",
}) {
  return (
    <div className="flex flex-col items-center gap-7 text-center">
      {topText ? (
        <p
          className={`max-w-[330px] text-[14px] font-semibold leading-[1.45] text-white/88 ${topTextClassName}`}
        >
          {topText}
        </p>
      ) : null}

      <SocialCalibrationAnimation
        user={user}
        className={visualClassName}
      />

      {bottomText ? (
        <p
          className={`max-w-[330px] text-[14px] font-semibold leading-[1.45] text-white/82 ${bottomTextClassName}`}
        >
          {bottomText}
        </p>
      ) : null}

      <SocialCalibrationAnimationStyles />
    </div>
  );
}

function SocialCalibrationAnimationStyles() {
  return (
    <style>{`
      .vg-social-calibration-visual {
        position: relative;
        width: min(72vw, 260px);
        aspect-ratio: 1;
        margin: 0 auto;
        border-radius: 999px;
        overflow: hidden;
        background:
          radial-gradient(circle at 50% 50%, rgba(255,255,255,0.16), rgba(255,255,255,0.05) 36%, transparent 62%),
          linear-gradient(135deg, rgba(33, 101, 255, 0.20), rgba(27, 208, 164, 0.16));
        box-shadow:
          inset 0 0 42px rgba(255,255,255,0.08),
          0 18px 58px rgba(0,0,0,0.22);
      }

      .vg-social-calibration-ring,
      .vg-social-calibration-orbit,
      .vg-social-calibration-center {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }

      .vg-social-calibration-ring {
        border-radius: 999px;
        border: 1px solid rgba(255,255,255,0.16);
        animation: vgCalibrationPulse 3.2s ease-in-out infinite;
      }

      .vg-social-calibration-ring--outer {
        width: 82%;
        height: 82%;
        animation-delay: 0ms;
      }

      .vg-social-calibration-ring--middle {
        width: 61%;
        height: 61%;
        animation-delay: 260ms;
      }

      .vg-social-calibration-ring--inner {
        width: 40%;
        height: 40%;
        animation-delay: 520ms;
      }

      .vg-social-calibration-ray {
        position: absolute;
        left: 50%;
        top: 50%;
        width: 36%;
        height: 1px;
        transform-origin: left center;
        background: linear-gradient(90deg, rgba(255,255,255,0.46), rgba(255,255,255,0.03));
        animation: vgCalibrationRay 2.8s ease-in-out infinite;
      }

      .vg-social-calibration-ray--one {
        transform: rotate(-95deg);
        animation-delay: 120ms;
      }

      .vg-social-calibration-ray--two {
        transform: rotate(139deg);
        animation-delay: 540ms;
      }

      .vg-social-calibration-ray--three {
        transform: rotate(22deg);
        animation-delay: 920ms;
      }

      .vg-social-calibration-node {
        position: absolute;
        width: 22px;
        height: 22px;
        border-radius: 999px;
        border: 1px solid rgba(255,255,255,0.54);
        background: rgba(255,255,255,0.16);
        box-shadow: inset 0 0 16px rgba(255,255,255,0.16);
        transform: translate(-50%, -50%);
      }

      .vg-social-calibration-node--one {
        left: 47%;
        top: 14%;
      }

      .vg-social-calibration-node--two {
        left: 22%;
        top: 75%;
      }

      .vg-social-calibration-node--three {
        left: 84%;
        top: 64%;
      }

      .vg-social-calibration-orbit {
        width: 72%;
        height: 72%;
        border-radius: 999px;
        animation: vgCalibrationOrbit 8.4s linear infinite;
      }

      .vg-social-calibration-orbit span {
        position: absolute;
        left: 50%;
        top: -5px;
        width: 10px;
        height: 10px;
        border-radius: 999px;
        background: #ffffff;
        box-shadow: 0 0 18px rgba(255,255,255,0.66);
      }

      .vg-social-calibration-orbit--two {
        width: 58%;
        height: 58%;
        animation-duration: 6.8s;
        animation-direction: reverse;
      }

      .vg-social-calibration-orbit--two span {
        width: 8px;
        height: 8px;
        background: #bdf7e6;
      }

      .vg-social-calibration-orbit--three {
        width: 86%;
        height: 86%;
        animation-duration: 10.2s;
        animation-delay: -1.1s;
      }

      .vg-social-calibration-orbit--three span {
        width: 7px;
        height: 7px;
        background: #c8d9ff;
      }

      .vg-social-calibration-center {
        z-index: 3;
        display: flex;
        width: 76px;
        height: 76px;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        border-radius: 999px;
        border: 2px solid rgba(255,255,255,0.84);
        background: linear-gradient(135deg, #25324a, #111827);
        color: #ffffff;
        font-size: 1.25rem;
        font-weight: 800;
        letter-spacing: 0.08em;
        box-shadow: 0 10px 36px rgba(0,0,0,0.32);
        animation: vgCalibrationCenter 3.4s ease-in-out infinite;
      }

      @keyframes vgCalibrationPulse {
        0%, 100% {
          opacity: 0.48;
          transform: translate(-50%, -50%) scale(0.96);
        }
        50% {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1.03);
        }
      }

      @keyframes vgCalibrationOrbit {
        to {
          transform: translate(-50%, -50%) rotate(360deg);
        }
      }

      @keyframes vgCalibrationRay {
        0%, 100% {
          opacity: 0.24;
          width: 27%;
        }
        50% {
          opacity: 0.86;
          width: 36%;
        }
      }

      @keyframes vgCalibrationCenter {
        0%, 100% {
          transform: translate(-50%, -50%) scale(1);
        }
        50% {
          transform: translate(-50%, -50%) scale(1.04);
        }
      }
    `}</style>
  );
}
